import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import localForage from "localforage";
import "dayjs/locale/en-gb";
import { locale } from "dayjs";
import App from "@/App";
import RequireAuth from "@/auth/RequireAuth";
import SecurityProvider from "@/auth/SecurityProvider";
import { ProjectLockedError } from "@/api/apiClient";
import { AxiosProvider } from "@/contexts/AxiosContext";
import { MediaGenerationErrorProvider } from "@/contexts/MediaGenerationErrorProvider";
import { enqueueMuiToast, MuiSnackbarProvider } from "@/contexts/MuiSnackbarProvider";
import { StoreProvider } from "@/contexts/StoreContext";
import LoadingPage from "@/pages/Loading";
import { RootStore } from "@/stores/RootStore";
import theme from "@/theme";
import { coloredConsole } from "@/utils/utils";
import "@/css/globals.css";
import "@/css/editor.css";
import "@mdxeditor/editor/style.css";
import "video.js/dist/video-js.css";
import "@/css/videojs.css";
import { Suspense } from "react";

const rootStore = new RootStore();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 5 * 60 * 1000, // 5 minutes
      staleTime: 30 * 1000, // 30 seconds
      retry: 1,
      refetchOnWindowFocus: false
    },
    mutations: {
      retry: 1,
      // Global 423 handling: any write mutation on an owner-locked project throws a
      // ProjectLockedError. Only refetch the project if our own state doesn't already show it
      // as locked - otherwise this is just the expected error for a state we already know about.
      onError: (error) => {
        if (error instanceof ProjectLockedError) {
          enqueueMuiToast(error.message, { variant: "error" });
          const details = rootStore.actionStore.projectDetails;
          if (details?.project_id && details.is_locked !== true) {
            void queryClient.invalidateQueries({ queryKey: ["project", details.project_id] });
          }

          void queryClient.invalidateQueries({ queryKey: ["projects"] });
        }
      }
    }
  }
});

/**
 * Excluded from persistence for two reasons: presigned URLs expire, so a persisted-then-rehydrated
 * entry can be stale; and `mediaBatch` queries resolve to a `Map`, which `JSON.stringify` (used by
 * the persister) silently flattens to `{}` on write - rehydrating that on the next page load leaves
 * `data` looking present but with `.get` missing, crashing any reader expecting a real Map.
 */
function isPersistedMediaEntityQueryKey(queryKey: readonly unknown[]): boolean {
  const kind = queryKey[0];
  return kind === "image" || kind === "video" || kind === "book" || kind === "mediaBatch";
}

const asyncStoragePersister = createAsyncStoragePersister({
  storage: localForage,
  throttleTime: 1000
});

coloredConsole(`App Version: ${import.meta.env.VITE_APP_VERSION}`, "lightblue");
coloredConsole(`Environment: ${import.meta.env.VITE_PUBLIC_ENV}`, "lightgreen");

const container = document.querySelector("#root");
if (container) {
  const root = createRoot(container);
  locale("en-gb");

  root.render(
    <Suspense fallback={<LoadingPage />}>
      <StoreProvider value={rootStore}>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister: asyncStoragePersister,
            buster: "2026-08-25-no-project-images-page",
            dehydrateOptions: {
              shouldDehydrateQuery: (query) => !isPersistedMediaEntityQueryKey(query.queryKey)
            }
          }}
        >
          <BrowserRouter>
            <SecurityProvider>
              <AxiosProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <MuiSnackbarProvider>
                      <MediaGenerationErrorProvider>
                        <RequireAuth>
                          <App />
                        </RequireAuth>
                      </MediaGenerationErrorProvider>
                    </MuiSnackbarProvider>
                  </ThemeProvider>
                </LocalizationProvider>
              </AxiosProvider>
            </SecurityProvider>
          </BrowserRouter>
        </PersistQueryClientProvider>
      </StoreProvider>
    </Suspense>
  );
}
